import type { Tour } from '~/helpers/tour/tours'
import { RoleEnum } from '@zeus/index'
import { Compass, LifeBuoy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { useBranding } from '~/helpers/branding/branding-context'
import { useAuth } from '~/helpers/store/auth'
import { useContactPanel } from '~/helpers/store/contact-panel'
import { TOURS, toursForRole } from '~/helpers/tour/tours'

/**
 * Le centre d'aide (#78), sans cadre : le back-office le monte dans son layout,
 * l'espace apprenant dans le sien (`LearnerHelpPage`).
 *
 * Il ne propose que les visites des écrans que le rôle peut ouvrir. Lancer une
 * visite, c'est ouvrir l'écran avec `?tour=<id>` : `TourLauncher` prend le relais
 * une fois l'écran chargé. En bas, l'accès au formulaire d'aide (#81).
 */
function HelpView({ tours = TOURS }: { tours?: Tour[] }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const role = useAuth(state => state.payload?.role)
  const capabilities = useAuth(state => state.capabilities)
  const { billingSelfService } = useBranding()
  const openContact = useContactPanel(state => state.open)
  const available = toursForRole(role, tours, capabilities, { billingSelfService })

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-2xl text-sm text-muted-foreground">{t('help.intro')}</p>

      <section className="flex flex-col gap-3" aria-labelledby="help-tours">
        <h2 id="help-tours" className="font-heading text-lg font-semibold">{t('help.tours.title')}</h2>
        {available.length === 0
          ? <p className="text-sm text-muted-foreground">{t('help.tours.empty')}</p>
          : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {available.map(tour => (
                  <Card key={tour.id} className="flex flex-col">
                    <CardHeader>
                      <CardTitle as="h3" className="flex items-center gap-2 text-base">
                        <Compass className="size-4 shrink-0 text-muted-foreground" />
                        {t(tour.titleKey)}
                      </CardTitle>
                      <CardDescription>{t(tour.descriptionKey)}</CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`${tour.route}?tour=${tour.id}`)}
                        aria-label={t('help.tour.startNamed', { name: t(tour.titleKey) })}
                      >
                        {t('help.tour.start')}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LifeBuoy className="size-4 shrink-0 text-muted-foreground" />
            {t('help.contact.title')}
          </CardTitle>
          <CardDescription>
            {role === RoleEnum.User ? t('contact.recipient.admins') : t('contact.recipient.support')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={openContact}>
            {role === RoleEnum.User ? t('help.contact.admins') : t('help.contact.support')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default HelpView
